import React, { useState } from 'react';
import {
    Box,
    Grid,
    Typography,
    Button,
    Card,
    CardContent,
    FormControlLabel,
    Checkbox,
    Select,
    MenuItem,
    FormControl,
    IconButton,
    TextField,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { allViolation, northAmericaCountries, CanadaProvinces, USAStates, MexicoStates } from '../../utils/dropOptions';

function TrafficViolations({ onNext }) {
    const [violations, setViolations] = useState([{
        violationDate: null,
        violationType: '',
        country: '',
        provinceOrState: '',
        commercialVehicle: false,
        penaltiesApplied: false,
        penaltiesDescription: '',
    }]);

    const getStateOptions = (country) => {
        switch (country) {
            case 'Canada':
                return CanadaProvinces;
            case 'U.S.A':
                return USAStates;
            case 'Mexico':
                return MexicoStates;
            default:
                return [];
        }
    };

    const handleChange = (index) => (field) => (event) => {
        const newViolations = [...violations];
        newViolations[index] = {
            ...newViolations[index],
            [field]: event.target.type === 'checkbox' ? event.target.checked : event.target.value,
            // Reset province/state when country changes
            ...(field === 'country' ? { provinceOrState: '' } : {})
        };
        setViolations(newViolations);
    };

    const handleDateChange = (index) => (date) => {
        const newViolations = [...violations];
        newViolations[index] = {
            ...newViolations[index],
            violationDate: date
        };
        setViolations(newViolations);
    };

    const addViolation = () => {
        setViolations([...violations, {
            violationDate: null,
            violationType: '',
            country: '',
            provinceOrState: '',
            commercialVehicle: false,
            penaltiesApplied: false,
            penaltiesDescription: '',
        }]);
    };

    const removeViolation = (index) => {
        if (violations.length > 1) {
            setViolations(violations.filter((_, i) => i !== index));
        }
    };

    return (
        <Box sx={{ maxWidth: 900, mx: 'auto', p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Traffic Violations (Last 3 Years)</Typography>
                <Button
                    variant="outlined"
                    onClick={addViolation}
                    startIcon={<AddIcon />}
                >
                    Add Violation
                </Button>
            </Box>

            {violations.map((violation, index) => (
                <Card variant="outlined" sx={{ mb: 3 }} key={index}>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="subtitle1">Violation {index + 1}</Typography>
                            {violations.length > 1 && (
                                <IconButton onClick={() => removeViolation(index)} size="small">
                                    <DeleteIcon />
                                </IconButton>
                            )}
                        </Box>

                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        label="Violation Date"
                                        value={violation.violationDate}
                                        onChange={handleDateChange(index)}
                                        renderInput={(params) => <TextField {...params} fullWidth required />}
                                    />
                                </LocalizationProvider>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth required>
                                    <Select
                                        value={violation.violationType}
                                        onChange={handleChange(index)('violationType')}
                                        displayEmpty
                                    >
                                        <MenuItem value="" disabled>Select Violation Type</MenuItem>
                                        {allViolation.map((type) => (
                                            <MenuItem key={type} value={type}>
                                                {type.replace(/_/g, ' ').toUpperCase()}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth required>
                                    <Select
                                        value={violation.country}
                                        onChange={handleChange(index)('country')}
                                        displayEmpty
                                    >
                                        <MenuItem value="" disabled>Select Country</MenuItem>
                                        {northAmericaCountries.map((country) => (
                                            <MenuItem key={country} value={country}>
                                                {country}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth required disabled={!violation.country}>
                                    <Select
                                        value={violation.provinceOrState}
                                        onChange={handleChange(index)('provinceOrState')}
                                        displayEmpty
                                    >
                                        <MenuItem value="" disabled>Select State/Province</MenuItem>
                                        {getStateOptions(violation.country).map((state) => (
                                            <MenuItem key={state} value={state}>
                                                {state}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={violation.commercialVehicle}
                                            onChange={handleChange(index)('commercialVehicle')}
                                        />
                                    }
                                    label="Commercial Vehicle Involved"
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={violation.penaltiesApplied}
                                            onChange={handleChange(index)('penaltiesApplied')}
                                        />
                                    }
                                    label="Penalties Applied"
                                />
                            </Grid>

                            {violation.penaltiesApplied && (
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={2}
                                        label="Describe Penalties"
                                        value={violation.penaltiesDescription}
                                        onChange={handleChange(index)('penaltiesDescription')}
                                        required
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </CardContent>
                </Card>
            ))}

            <Box sx={{ mt: 3 }}>
                <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={() => onNext(violations)}
                >
                    Continue
                </Button>
            </Box>
        </Box>
    );
}

export default TrafficViolations;
